import { Alignment, EventType, Fit, Layout, useRive } from "@rive-app/react-canvas";
import styled, { css } from "styled-components";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const RIVE_FILE_PATH = "/beam_v2.riv";
const STATE_MACHINE_NAME = "State Machine 1";
const CLIPBOARD_EVENTS = {
  PYTHON: "Clipboard 1 click (Python)",
  TYPESCRIPT: "Clipboard 2 click (TypeScript)",
} as const;

const CODE_SNIPPETS = {
  [CLIPBOARD_EVENTS.PYTHON]: `from beam import sandbox

# Create a sandbox
sb = sandbox.Sandbox()

# Run code in the sandbox
result = sb.run("print('Hello from Beam!')")
print(result.stdout)`,
  [CLIPBOARD_EVENTS.TYPESCRIPT]: `import Beam from "@beam-cloud/sdk";

const beam = new Beam();

// Create a sandbox
const sb = await beam.sandbox.create();

// Run code in the sandbox
const result = await sb.run("console.log('Hello from Beam!')");
console.log(result.stdout);`,
} as const;

const RiveWrapper = () => {
  const [currentAnimation, setCurrentAnimation] = useState(0);
  const [animations, setAnimations] = useState<{ name: string; stateMachineName: string }[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const riveContainerRef = useRef<HTMLDivElement>(null);

  const { rive, RiveComponent } = useRive({
    src: RIVE_FILE_PATH,
    stateMachines: STATE_MACHINE_NAME,
    autoplay: true,
    artboard: "Hero v2 - Main artboard 2x",
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
    onLoad: () => {
      rive?.resizeDrawingSurfaceToCanvas();
      setTimeout(() => setIsLoaded(true), 100);
    },
  });

  const resizeRiveCanvas = () => {
    if (!rive || !riveContainerRef.current) return;
    const container = riveContainerRef.current;

    const canvas = container.querySelector("canvas");
    if (!canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;
    canvas.width = Math.floor(containerWidth * pixelRatio);
    canvas.height = Math.floor(containerHeight * pixelRatio);

    // Scale the drawing context
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(pixelRatio, pixelRatio);
    }

    // Tell Rive to resize
    rive.resizeDrawingSurfaceToCanvas();
    rive.startRendering();
  };

  const copyCodeToClipboard = useCallback(async (clipboardEvent: string) => {
    try {
      const snippet = CODE_SNIPPETS[clipboardEvent as keyof typeof CODE_SNIPPETS];
      if (snippet) {
        await navigator.clipboard.writeText(snippet);
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  }, []);

  useEffect(() => {
    if (rive) {
      const availableAnimations = [
        { name: "TAB-1", stateMachineName: STATE_MACHINE_NAME },
        { name: "TAB-2", stateMachineName: STATE_MACHINE_NAME },
        { name: "TAB-3", stateMachineName: STATE_MACHINE_NAME },
      ];
      rive.on(EventType.RiveEvent, event => {
        if (
          event.data &&
          typeof event.data === "object" &&
          "name" in event.data &&
          typeof event.data.name === "string"
        ) {
          const eventName = (event.data as { name: string }).name.trim();
          if (eventName === CLIPBOARD_EVENTS.PYTHON || eventName === CLIPBOARD_EVENTS.TYPESCRIPT) {
            copyCodeToClipboard(eventName);
          }
        }
      });
      setAnimations(availableAnimations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rive]);

  // Play a specific animation
  const playAnimation = (index: number) => {
    if (!rive || index >= animations.length) return;
    try {
      const animation = animations[index];
      rive.play(animation.name);
      setCurrentAnimation(index);
    } catch (error) {
      console.error("Error playing animation:", error);
    }
  };

  const handleAnimationClick = () => {
    if (animations.length === 0) return;
    const nextIndex = (currentAnimation + 1) % animations.length;
    playAnimation(nextIndex);
  };

  return (
    <RiveContainer
      ref={riveContainerRef}
      onClick={handleAnimationClick}
      onTransitionEnd={resizeRiveCanvas}
      style={{ cursor: "pointer" }}
      $isLoaded={isLoaded}
    >
      <RiveComponent />
    </RiveContainer>
  );
};

export default RiveWrapper;

const RiveContainer = styled.div<{ $isLoaded: boolean }>`
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: scale(0.95) translateY(20px);
  transition: opacity 0.8s ease-out, transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 24px;
  overflow: hidden;
  ${props =>
    props.$isLoaded &&
    css`
      opacity: 1;
      transform: scale(1) translateY(0);
    `}
`;
